import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './contexts/AuthContext';
import ConfluenceButton from './components/ConfluenceButton';
import LoginPage from './pages/LoginPage';
import ProductsPage from './pages/ProductsPage';
import TutorialsPage from './pages/TutorialsPage';
import TutorialViewer from './pages/TutorialViewer';
import TutorialBuilder from './pages/TutorialBuilder';
import AdminPage from './pages/AdminPage';
import Dashboard from './pages/Dashboard';
import CourseCatalog from './pages/CourseCatalog';
import DraftsPage from './pages/DraftsPage';
import AIAssistedBuilder from './pages/AIAssistedBuilder';
import CategoriesPage from './pages/CategoriesPage';
import CategoryDetailPage from './pages/CategoryDetailPage';
import CollectionsPage from './pages/CollectionsPage';
import CollectionDetailPage from './pages/CollectionDetailPage';
import GlossaryPage from './pages/GlossaryPage';
import glossaryService from './services/glossaryService';
// Content creation is handled by TutorialBuilder
// Shepherd.js removed

const App: React.FC = () => {
  const { isLoading } = useAuth();

  // Prefetch glossary data on app startup
  useEffect(() => {
    glossaryService.refreshFromAPI().catch((error) => {
      console.error('Failed to prefetch glossary:', error);
    });
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#667eea] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <Layout>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          
          {/* Public routes */}
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/categories/:categoryName" element={<CategoryDetailPage />} />
          <Route path="/collections" element={<CollectionsPage />} />
          <Route path="/collections/:id" element={<CollectionDetailPage />} />
          <Route path="/tutorials/:id" element={<TutorialViewer />} />
          <Route path="/glossary" element={<GlossaryPage />} />
          <Route path="/glossary/:term" element={<GlossaryPage />} />

          {/* Protected routes */}
          <Route path="/" element={<ProtectedRoute><Navigate to="/tutorials" replace /></ProtectedRoute>} />
          <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
          <Route path="/tutorials" element={<ProtectedRoute><TutorialsPage /></ProtectedRoute>} />
          {/* /create route removed - now handled by modal */}
          <Route path="/builder" element={<ProtectedRoute><TutorialBuilder /></ProtectedRoute>} />
          <Route path="/builder/:id" element={<ProtectedRoute><TutorialBuilder /></ProtectedRoute>} />
          <Route path="/builder/ai-assisted" element={<ProtectedRoute><AIAssistedBuilder /></ProtectedRoute>} />
          <Route path="/builder/ai-assisted/:id" element={<ProtectedRoute><AIAssistedBuilder /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/products" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/tutorials" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/hierarchy" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/collections" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/templates" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/glossary" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/media" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/manage" element={<ProtectedRoute><Navigate to="/admin/products" replace /></ProtectedRoute>} />
          <Route path="/manage/:tab" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          {/* Redirect legacy settings route to unified admin */}
          <Route path="/settings" element={<ProtectedRoute><Navigate to="/admin/settings" replace /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/catalog" element={<ProtectedRoute><CourseCatalog /></ProtectedRoute>} />
          <Route path="/drafts" element={<ProtectedRoute><DraftsPage /></ProtectedRoute>} />
          {/* Redirect legacy users route to unified admin */}
          <Route path="/users" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        </Routes>
      </Layout>
      <ConfluenceButton />
      <Toaster position="top-right" />
    </div>
  );
};

export default App;
